import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import LogoTiktok from '@/assets/icons/logoTiktok.svg?react';
import LogoInstagram from '@/assets/icons/logoInstagram.svg?react';
import LogoLinkedin from '@/assets/icons/logoLinkedin.svg?react';
import EnvelopeIcon from '@/assets/icons/envelop-rounded.svg?react';
import logoEnisa from '@/assets/images/logo-enisa.jpg';
import logoEnisaWebp from '@/assets/images/logo-enisa.webp';
import logoIata from '@/assets/images/logo-iata.svg';
import LanguageSelector from '../languageSelector/LanguageSelector';
import { toggleLogPrices } from '../../../redux/planSlice';
import plans from '../../../database/plans';
import useRoutes from '../../../hooks/useRoutes';
import styles from './Footer.module.scss';
import i18n from '@/i18n/config';

export const socialNetworks = [
    { name: 'instagram', icon: <LogoInstagram className={styles.logoIcon} /> },
    { name: 'tiktok', icon: <LogoTiktok className={styles.logoIcon} /> },
    { name: 'linkedin', icon: <LogoLinkedin className={styles.logoIcon} /> },
];

const activePlans = plans.filter((plan) => plan.active).toSorted((a, b) => a.order - b.order);
const plansLinks = activePlans.map((plan) => ({ id: plan.id, title: plan.title }));
const currentYear = new Date().getFullYear();

const Footer = () => {
    const dispatch = useAppDispatch();
    const logPrices = useAppSelector((state) => state.plan.prices.logPrices);
    const isInUS = useAppSelector((state) => state.booking.isInUS);
    const { t } = useTranslation();
    const { routes, experiencesInfoRoutes, isLandingMarketing, isLandingB2B } = useRoutes();
    const isLandingPage = isLandingMarketing;
    const { language } = i18n;

    const pagesLinks = [
        { name: t('hotels'), route: routes.hotels },
        { name: t('airlines'), route: routes.airlines },
        { name: t('reviews'), route: routes.reviews },
        { name: t('contactUs'), route: routes.contact },
        { name: t('helpFaqs'), route: routes.help },
        { name: t('termsConditions'), route: routes.terms },
        { name: t('privacyPolicy'), route: routes.privacy },
    ];

    const onEnvelopClick = (e: any) => {
        if (e.detail === 3) {
            const msg = logPrices ? 'Prices log desactivated' : 'Prices log activated';
            console.log(msg);
            dispatch(toggleLogPrices());
        }
    };

    return (
        <footer className={styles.footerWrapper}>
            <div className={styles.contentFooter}>
                <div className={styles.columnLogos}>
                    <div>
                        <h3 className={styles.columnTitle}>{t('followUs')}</h3>
                        <div className={styles.logosWrapper}>
                            {socialNetworks.map((network) => (
                                <a
                                    href={t(network.name)}
                                    target="_blank"
                                    rel="noreferrer"
                                    key={network.name}
                                    aria-label={network.name}
                                >
                                    {network.icon}
                                </a>
                            ))}
                        </div>
                    </div>
                    <div className={`${styles.columnLinks} ${styles.desktopVisible}`}>
                        <h3 className={`${styles.columnTitle} ${styles.marginTop}`}>{t('kubeEcosystem')}</h3>
                        <a href="https://kubecoin.org/" className={styles.link} target="_blank" rel="noreferrer">
                            {t('kubeCoin')}
                        </a>
                        <a href="https://eatkube.com/" className={styles.link} target="_blank" rel="noreferrer">
                            {t('eatKube')}
                        </a>
                        <a href="https://kubein.vc/" className={styles.link} target="_blank" rel="noreferrer">
                            {t('kubeIn')}
                        </a>
                    </div>
                </div>
                <div className={styles.columnLinks}>
                    <h3 className={styles.columnTitle}>{t('about')}</h3>
                    {pagesLinks.map((link) => (
                        <Link to={link.route} className={styles.link} key={link.name}>
                            {link.name}
                        </Link>
                    ))}
                </div>
                <div className={styles.columnLinks}>
                    <h3 className={styles.columnTitle}>{t('sections')}</h3>
                    {plansLinks.map((plan) => (
                        <Link to={experiencesInfoRoutes[plan.id]} key={plan.title} className={styles.link}>
                            {t(plan.title)}
                        </Link>
                    ))}

                    <Link to={routes.gift} className={styles.link}>
                        {t('kubegift')}
                    </Link>
                    <Link to={routes.landingB2B} className={styles.link}>
                        {t('landingB2B')}
                    </Link>
                    {language === 'es' ? (
                        <Link
                            to="https://www.descuentoestudiante.com/flykube-descuento-estudiante-iframe"
                            className={styles.link}
                            target="blanck"
                        >
                            {t('studentDiscount')}
                        </Link>
                    ) : (
                        ''
                    )}
                </div>
                <div className={styles.lastColumn}>
                    <div className={`${styles.columnLinks} ${styles.mobileVisible}`}>
                        <h3 className={`${styles.columnTitle} ${styles.marginTop}`}>{t('kubeEcosystem')}</h3>
                        <a href="https://kubecoin.org/" className={styles.link} target="_blank" rel="noreferrer">
                            {t('kubeCoin')}
                        </a>
                        <a href="https://eatkube.com/" className={styles.link} target="_blank" rel="noreferrer">
                            {t('eatKube')}
                        </a>
                        <a href="https://kubein.vc/" className={styles.link} target="_blank" rel="noreferrer">
                            {t('kubeIn')}
                        </a>
                    </div>
                    <div className={styles.flagsContactWrapper}>
                        <div className={styles.contactEmail}>
                            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                            <button onClick={onEnvelopClick} className={styles.envelop}>
                                <EnvelopeIcon />
                            </button>
                            {isLandingB2B ? 'business@flykube.com' : 'info@flykube.com'}
                        </div>
                        {!isInUS && !isLandingPage && (
                            <div className={styles.flagsMenu}>
                                <LanguageSelector />
                            </div>
                        )}

                        <picture>
                            <source srcSet={logoEnisaWebp} type="image/webp" />
                            <source srcSet={logoEnisa} type="image/jpeg" />
                            <img src={logoEnisa} alt="" className={styles.logoEnisa} loading="lazy" />
                        </picture>
                        <img src={logoIata} className={styles.logoIata} loading="lazy" alt="" />
                    </div>
                </div>
            </div>
            <div className={styles.copyright}>
                {currentYear} KUBE ECOSYSTEM SL © All rights reserved <p>Gran Via 415, 08015, Barcelona, Spain</p>
            </div>
        </footer>
    );
};

export default Footer;
